import { useState, useEffect } from 'react';
import axios from 'axios';
import RichTextEditor from '../components/Editor/Editor.jsx';
import MainLayout from '../components/MainLayout';
import { FileEdit, Eye, Save, Plus, Trash2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NoticeTemplates = ({ user, onLogout }) => {
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    template_name: '',
    notice_type: 'Demand Notice',
    stage: '',
    content: ''
  });
  const [previewData, setPreviewData] = useState({
    template_id: '',
    case_number: '',
    agreement_no: '',
    preview: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/notices/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        await axios.put(`${API_URL}/notices/templates/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage('Template updated successfully');
      } else {
        await axios.post(`${API_URL}/notices/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage('Template created successfully');
      }
      
      setShowForm(false);
      setFormData({ template_name: '', notice_type: 'Demand Notice', stage: '', content: '' });
      setEditingId(null);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setFormData({
      template_name: template.template_name,
      notice_type: template.notice_type,
      stage: template.stage || '',
      content: template.content
    });
    setEditingId(template._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/notices/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Template deleted successfully');
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handlePreview = async () => {
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/notices/templates/preview`, previewData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData({ ...previewData, preview: res.data.preview });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const insertVariable = (variable) => {
    setFormData({ ...formData, content: formData.content + `{{${variable}}}` });
  };

  const commonVariables = [
    'caseNumber', 'agreementNo', 'claimantName', 'claimantAddress',
    'arName', 'arbitratorName', 'respondentName', 'respondentPhone',
    'stage', 'amount', 'date'
  ];

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1 className="page-title">Notice Templates</h1>
        <p className="page-description">Create and manage notice templates with dynamic variables</p>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <div style={{ marginBottom: '1.5rem' }}>
        <button
          data-testid="create-template-btn"
          className="btn-icon"
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setFormData({ template_name: '', notice_type: 'Demand Notice', stage: '', content: '' });
          }}
        >
          <Plus size={18} />
          Create New Template
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header">
            <h2 className="card-title">
              {editingId ? 'Edit Template' : 'Create New Template'}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label>Template Name</label>
                <input
                  data-testid="template-name"
                  className="form-input"
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  required
                  placeholder="e.g., Standard Demand Notice"
                />
              </div>

              <div className="form-group">
                <label>Notice Type</label>
                <select
                  data-testid="notice-type"
                  className="form-select"
                  value={formData.notice_type}
                  onChange={(e) => setFormData({ ...formData, notice_type: e.target.value })}
                >
                  <option value="Demand Notice">Demand Notice</option>
                  <option value="Loan Recall Notice">Loan Recall Notice</option>
                  <option value="Pre-ODR / Section 21 Notice">Pre-ODR / Section 21 Notice</option>
                  <option value="Directions">Directions</option>
                  <option value="Order">Order</option>
                  <option value="Award">Award</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Stage (Optional)</label>
              <select
                data-testid="stage-select"
                className="form-select"
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
              >
                <option value="">Select Stage</option>
                <optgroup label="Stage 0 - Initiation">
                  <option value="0">Stage 0 – Initiation and Invitation of Arbitration</option>
                  <option value="0A">Stage 0A – Appointment of Arbitrator</option>
                </optgroup>
                
                <optgroup label="Stage 1 - Directions">
                  <option value="1">Stage 1 – Directions for Submitting Claim: Timelines and Procedure Established</option>
                </optgroup>
                
                <optgroup label="Stage 2 - Claim & IA">
                  <option value="2">Stage 2 – Claim and IA (if any) Submitted: Directions for Reply</option>
                  <option value="2A">Stage 2A – Reply Not Submitted: Interim Order (IO) Passed, Last Opportunity for Reply</option>
                  <option value="2B">Stage 2B – Reply Not Submitted: SOD Closed, Ex-Parte Issues Framed...</option>
                  <option value="2C">Stage 2C – Claimant Evidence Submitted: Proceedings Closed/Reserved</option>
                  <option value="2D">Stage 2D – Ex-Parte Award</option>
                </optgroup>
                
                <optgroup label="Stage 3 - Reply & Evidence">
                  <option value="3">Stage 3 – Reply Submitted: IO Passed, Directions for Rejoinder</option>
                  <option value="3A">Stage 3A – Rejoinder Filed: Issues Framed, Directions for Claimant’s Evidence</option>
                  <option value="3B">Stage 3B – Rejoinder Not Filed: Issues Framed, Directions for Claimant’s Evidence</option>
                  <option value="3C">Stage 3C – Claimant Evidence Submitted: Directions for Respondent’s Evidence</option>
                  <option value="3D">Stage 3D – Respondent Evidence Submitted: Proceedings Closed, Reserved</option>
                  <option value="3E">Stage 3E – Respondent Evidence Not Submitted: Proceedings Closed, Reserved</option>
                  <option value="3F">Stage 3F – Final Award – Claim Allowed</option>
                  <option value="3G">Stage 3G – Final Award – Claim Rejected</option>
                  <option value="3H">Stage 3H – Settlement Award</option>
                  <option value="3I">Stage 3I – Withdrawal of Case</option>
                </optgroup>
                
                <optgroup label="Stage 4 - Objections">
                  <option value="4">Stage 4 – Objection Petition Filed: Direction for Reply</option>
                  <option value="4A">Stage 4A – Reply Filed: Direction for Rejoinder</option>
                  <option value="4B">Stage 4B – Rejoinder Submitted: Direction for Oral Hearing</option>
                  <option value="4C">Stage 4C – Oral Hearing Conducted: Order Reserved on Objections</option>
                  <option value="4D">Stage 4D – Objection Rejected: Restored</option>
                  <option value="4E">Stage 4E – Objection Accepted: Proceedings Terminated</option>
                </optgroup>
                
                <optgroup label="Stage 5 - Interim Applications">
                  <option value="5">Stage 5 – IA Submitted: Direction for Reply</option>
                  <option value="5A">Stage 5A – Reply Not Submitted: IO Passed, Proceedings Restored</option>
                  <option value="5B">Stage 5B – Reply Submitted: Direction for Rejoinder</option>
                  <option value="5C">Stage 5C – Rejoinder Not Submitted: IO Passed, Proceedings Restored</option>
                  <option value="5D">Stage 5D – Rejoinder Submitted: IO Passed, Proceedings Restored</option>
                </optgroup>
                
                <optgroup label="Stage 6 - Amendments">
                  <option value="6">Stage 6 – Amendment Application in Claim Filed: Direction for Reply</option>
                  <option value="6A">Stage 6A – Reply Not Filed: Application Allowed</option>
                  <option value="6B">Stage 6B – Reply Filed: Direction for Rejoinder</option>
                  <option value="6C">Stage 6C – Rejoinder Not Filed: Application Allowed</option>
                  <option value="6D">Stage 6D – Rejoinder Filed: Application Allowed</option>
                  <option value="6E">Stage 6E – Amendment Application Rejected</option>
                </optgroup>
                
                <optgroup label="Stage 7 - Oral Hearing">
                  <option value="7">Stage 7 – Oral Hearing Scheduled</option>
                  <option value="7A">Stage 7A – Oral Hearing Conducted</option>
                </optgroup>
              </select>
            </div>

            <div className="form-group">
              <label>Template Content</label>
              <div style={{ marginBottom: '1rem' }}>
                <small style={{ color: '#718096' }}>
                  Insert variables: {commonVariables.map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => insertVariable(v)}
                      style={{
                        background: '#e2e8f0',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        margin: '0.25rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </small>
              </div>
              <div style={{ minHeight: '300px', marginBottom: '3rem' }}>
                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Enter template content..."
                  height={400}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                data-testid="save-template"
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                <Save size={18} style={{ marginRight: '0.5rem' }} />
                {loading ? 'Saving...' : (editingId ? 'Update Template' : 'Save Template')}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ template_name: '', notice_type: 'Demand Notice', stage: '', content: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Templates Library</h2>
        </div>

        {templates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><FileEdit size={48} /></div>
            <div className="empty-state-text">No templates created yet</div>
          </div>
        ) : (
          <table data-testid="templates-table" className="data-table">
            <thead>
              <tr>
                <th>Template Name</th>
                <th>Notice Type</th>
                <th>Stage</th>
                <th>Variables</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template._id}>
                  <td style={{ fontWeight: 600 }}>{template.template_name}</td>
                  <td>{template.notice_type}</td>
                  <td>{template.stage || '-'}</td>
                  <td>
                    <span className="badge badge-new">
                      {template.variables?.length || 0} variables
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        data-testid="preview-template"
                        className="btn-secondary"
                        onClick={() => {
                          setPreviewData({ ...previewData, template_id: template._id });
                          setShowPreview(true);
                        }}
                        style={{ padding: '0.5rem', fontSize: '0.75rem' }}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        data-testid="edit-template"
                        className="btn-secondary"
                        onClick={() => handleEdit(template)}
                        style={{ padding: '0.5rem', fontSize: '0.75rem' }}
                      >
                        <FileEdit size={16} />
                      </button>
                      <button
                        data-testid="delete-template"
                        className="btn-secondary"
                        onClick={() => handleDelete(template._id)}
                        style={{ padding: '0.5rem', fontSize: '0.75rem', background: '#fc8181', color: 'white' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>Preview Template</h2>
              <button
                onClick={() => setShowPreview(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Case Number</label>
                  <input
                    data-testid="preview-case-number"
                    className="form-input"
                    value={previewData.case_number}
                    onChange={(e) => setPreviewData({ ...previewData, case_number: e.target.value })}
                    placeholder="Enter case number"
                  />
                </div>
                <div className="form-group">
                  <label>Agreement Number</label>
                  <input
                    data-testid="preview-agreement-no"
                    className="form-input"
                    value={previewData.agreement_no}
                    onChange={(e) => setPreviewData({ ...previewData, agreement_no: e.target.value })}
                    placeholder="Enter agreement number"
                  />
                </div>
              </div>
              <button
                data-testid="generate-preview"
                className="btn-primary"
                onClick={handlePreview}
                style={{ marginTop: '1rem' }}
              >
                <Eye size={18} style={{ marginRight: '0.5rem' }} />
                Generate Preview
              </button>
            </div>

            {previewData.preview && (
              <div 
                style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '2rem',
                  minHeight: '300px'
                }}
                dangerouslySetInnerHTML={{ __html: previewData.preview }}
              />
            )}
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          max-height: 90vh;
          overflow-y: auto;
        }
      `}</style>
    </MainLayout>
  );
};

export default NoticeTemplates;
